/**
 * BankAccountSettings — landlord & tenant page at /settings/bank.
 *
 * Wraps the Flinks Connect iframe in a settings shell so a landlord (for
 * rent-payouts) or tenant (for direct-debit / cashout) can link their
 * Canadian bank account. The iframe is mounted only after the backend
 * confirms `/api/flinks/status` is `ready`, otherwise we show a friendly
 * "not configured yet" panel.
 *
 * Wire-up:
 *   1. Backend mints a one-time iframe URL via POST /api/flinks/connect-session
 *   2. Iframe sends `postMessage` to the parent with the loginId after the
 *      user finishes the flow.
 *   3. We POST that loginId to /api/flinks/login-id which persists the link.
 *   4. The dashboard refreshes via the SSE channel ("cash-app" topic).
 */
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, Card, Button, Loader, EmptyState } from "@/components/ui/Primitives";
import { Landmark, CheckCircle2, AlertCircle, RefreshCw, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

export default function BankAccountSettings() {
  const { user, token } = useAuth();
  const [status, setStatus] = useState(null);
  const [iframeUrl, setIframeUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [links, setLinks] = useState([]);
  const [refreshing, setRefreshing] = useState(null);
  const iframeRef = useRef(null);

  const reload = async () => {
    try {
      const [s, l] = await Promise.all([
        fetch(`${API}/api/flinks/status`).then(r => r.json()).catch(() => null),
        api.get("/flinks/bank-links/me").then(r => r.data).catch(() => ({ bank_links: [] })),
      ]);
      setStatus(s);
      setLinks(l.bank_links || []);
    } catch (e) {
      console.warn("flinks status load failed", e);
    }
  };
  useEffect(() => { reload(); }, []);

  // Listen for the Flinks postMessage handshake.
  useEffect(() => {
    let saved = false; // Idempotency guard — Flinks fires multiple messages.
    const onMessage = async (e) => {
      // ── Origin check ── only accept messages from Flinks's iframe host,
      // otherwise unrelated iframes (chat widgets, analytics, etc.) can
      // inject fake loginIds.
      try {
        const host = new URL(e.origin || "").hostname;
        if (host && !host.endsWith(".fin.ag") && !host.endsWith(".flinks.com")) return;
      } catch { /* ignore origin parse */ }

      const data = e.data || {};
      // Flinks emits a stream of step events. Only the TERMINAL step
      // (REDIRECT) means the user finished the flow — earlier events like
      // ACCOUNT_SELECTED fire while the user is still picking an account
      // and saving on those leaves the link half-baked. Bug fix: gate on
      // REDIRECT (or step==="SUCCESS") only.
      const step = (data?.step || "").toUpperCase();
      const terminal = step === "REDIRECT" || step === "SUCCESS" || step === "COMPONENT_DONE";
      if (!terminal) return;

      const loginId = data.loginId || data.LoginId;
      const requestId = data.requestId || data.RequestId;
      const institution = data.institution || data.Institution;
      if (!loginId || saved) return;
      saved = true;
      try {
        await api.post("/flinks/login-id", { loginId, requestId, institution });
        toast.success("Bank account linked");
        setIframeUrl("");
        reload();
      } catch (err) {
        saved = false; // Allow retry on transient failure.
        toast.error(err?.response?.data?.detail || "Could not save link");
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  const linkBank = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/flinks/connect-session", {});
      setIframeUrl(data.iframeUrl);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start bank-link flow");
    } finally { setBusy(false); }
  };

  const refresh = async (id) => {
    setRefreshing(id);
    try {
      const { data } = await api.post(`/flinks/refresh-accounts/${id}`);
      toast.success(`Synced ${data.accounts || 0} accounts · ${data.transactions_inserted || 0} new transactions`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed");
    } finally { setRefreshing(null); }
  };

  if (!status) return <Loader/>;

  const ready = status.status === "ready";
  const isSandbox = (status.env || "").toUpperCase() !== "PRODUCTION";

  return (
    <div>
      <PageHeader
        title="Bank accounts"
        subtitle={user?.role === "tenant"
          ? "Link your Canadian bank for rent direct-debit and referral payouts."
          : "Link your operating account so rent payments and referral payouts land in the right place."}
        actions={
          ready ? (
            <Button onClick={linkBank} disabled={busy} data-testid="flinks-link-button">
              {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Landmark className="w-4 h-4"/>} Link a bank account
            </Button>
          ) : null
        }
      />

      {ready && isSandbox && (
        <Card className="p-4 mb-6 border-sky-200 bg-sky-50" data-testid="flinks-sandbox-notice">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-sky-600 flex-none mt-0.5"/>
            <div className="text-sm text-sky-900">
              <div className="font-display font-bold text-sky-900">Test mode — real banks aren&apos;t available yet</div>
              <p className="mt-1">
                We&apos;re running the bank-link flow in <strong>sandbox mode</strong> right now. That means the iframe only lists test institutions (Flinks Capital, Test National Bank, etc.), not your real RBC / TD / BMO / Scotia / CIBC account. This is expected until we receive the production credentials from Flinks.
              </p>
              <p className="mt-2 text-xs">
                To flip to <strong>production</strong>, an admin needs to add <code className="bg-white px-1.5 py-0.5 rounded">FLINKS_SECRET_KEY</code> to <code className="bg-white px-1.5 py-0.5 rounded">backend/.env</code> and set <code className="bg-white px-1.5 py-0.5 rounded">FLINKS_ENV=PRODUCTION</code>. Once Flinks replies with the key, every landlord + tenant will see the real bank list — RBC, TD, BMO, Scotia, CIBC, National Bank, Desjardins, Tangerine, and 900+ Canadian credit unions.
              </p>
            </div>
          </div>
        </Card>
      )}

      {!ready && (
        <Card className="p-5 mb-6 border-amber-200 bg-amber-50" data-testid="flinks-not-ready">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-none mt-0.5"/>
            <div>
              <div className="font-display font-bold text-amber-900">Bank-linking isn&apos;t switched on yet</div>
              <p className="text-sm text-amber-800 mt-1">
                Central Rentals connects your Canadian bank account through our compliant banking partner. Your admin needs to paste the production credentials into the backend environment, then this page will go live.
              </p>
              <code className="block mt-3 text-[11px] bg-white px-3 py-2 rounded border border-amber-200 text-slate-700">
                {`FLINKS_SECRET_KEY=…
FLINKS_IFRAME_BASE_URL=…
FLINKS_API_BASE_URL=…`}
              </code>
              {status.needs && (
                <div className="text-[11px] text-amber-700 mt-2">
                  Missing: <code>{status.needs.join(", ")}</code>
                </div>
              )}
            </div>
          </div>
        </Card>
      )}

      <Card className="p-4 mb-6">
        <div className="flex items-center gap-2 text-slate-700 text-sm">
          <ShieldCheck className="w-4 h-4 text-emerald-600"/>
          <span>
            <strong>256-bit TLS in transit, AES-256 at rest.</strong> We never see or store your banking password — Flinks handles the credentials, we only receive a read-only token.
          </span>
        </div>
      </Card>

      {iframeUrl ? (
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm relative isolate" data-testid="flinks-iframe-wrap" style={{ zIndex: 1 }}>
          <iframe
            ref={iframeRef}
            src={iframeUrl}
            title="Flinks Connect"
            data-testid="flinks-iframe"
            className="block w-full rounded-xl"
            style={{ height: 720, border: 0, pointerEvents: "auto" }}
            allow="clipboard-read; clipboard-write; payment; encrypted-media; fullscreen"
            allowFullScreen
            referrerPolicy="origin-when-cross-origin"
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation allow-modals allow-storage-access-by-user-activation"
          />
          <div className="px-4 py-2 border-t border-slate-100 text-[11px] text-slate-500 flex items-center justify-between">
            <span>Trouble interacting? Open the bank-link flow in a new tab.</span>
            <a href={iframeUrl} target="_blank" rel="noopener noreferrer" data-testid="flinks-open-new-tab" className="font-bold text-emerald-700 underline">Open in new tab →</a>
          </div>
        </div>
      ) : (
        <>
          {links.length === 0 ? (
            <EmptyState
              icon={Landmark}
              title="No linked accounts yet"
              description="Click ‘Link a bank account’ to connect your Canadian bank. Takes about 60 seconds."
              action={ready ? <Button onClick={linkBank}><Landmark className="w-4 h-4"/> Link a bank account</Button> : null}
            />
          ) : (
            <div className="space-y-3" data-testid="flinks-links-list">
              {links.map((l) => (
                <Card key={l.id} className="p-4" data-testid={`flinks-link-${l.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center">
                      <CheckCircle2 className="w-5 h-5"/>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-display font-bold text-slate-900">{l.institution || "Canadian bank"}</div>
                      <div className="text-[11px] text-slate-500">
                        Linked {new Date(l.created_at).toLocaleDateString("en-CA")} ·
                        {l.last_refreshed_accounts ? ` ${l.last_refreshed_accounts} accounts` : ""}
                      </div>
                    </div>
                    <Button variant="secondary" onClick={() => refresh(l.id)} disabled={refreshing === l.id} className="!px-3 !py-1.5 text-xs">
                      {refreshing === l.id ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <RefreshCw className="w-3.5 h-3.5"/>} Sync
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
